import { inject, Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, EMPTY, Observable, of, tap } from 'rxjs';

import { Identifier } from '../shared/models/storage.models';
import { LoggerService } from '../shared/services/logger.service';
import { GraphService, SSOLoginStatus } from './graph.service';
import { InitialStartupService } from './initial-startup.service';

/**
 * Signing out of an SSO portal means ending **two** sessions that are cleared by two different
 * mechanisms:
 *
 * 1. the Central Office session — the `user` slice and its localStorage mirror, cleared by
 *    `InitialStartupService.clearSession()`;
 * 2. the MSAL / Azure AD session — the `msal.*` cache (account keys, id token, access tokens and
 *    the **refresh token**) plus the AAD session cookie, cleared by `MsalService.logoutPopup()`.
 *
 * They used to be fired concurrently, with `authService.logout()` on a bare `setTimeout` — and
 * that call ends in `window.location.replace('/auth')`, which tears the document down. The
 * redirect always won: the CO session was cleared, MSAL's cache survived intact, and the reloaded
 * app found a live account and refresh token on `/auth` and silently signed the user straight back
 * in. Measured on a real SSO stack: all 10 MSAL entries still present after a "successful" logout.
 *
 * So the order here is load-bearing. `logoutPopup()` must be **subscribed** (`MsalService` returns
 * `from(instance.logoutPopup(...))`, so the popup opens either way — but nothing waits for it) and
 * must be allowed to finish before anything navigates. Its cache sweep is what removes
 * `msal.accToken`, and `AuthComponent.ssoAuthentication()` needs exactly that key to re-authenticate
 * — clearing it is what makes the sign-out stick.
 *
 * `mainWindowRedirectUri` is deliberately not set: this service owns the navigation, and handing
 * MSAL a second one puts the two in a race again.
 */
@Injectable({ providedIn: 'root' })
export class SsoLogoutService {
  private readonly msalService = inject(MsalService);
  private readonly graphService = inject(GraphService);
  private readonly startupService = inject(InitialStartupService);
  private readonly logger = inject(LoggerService);

  private isSsoSession(): boolean {
    return localStorage.getItem(Identifier.TYPE_IS_SSO_LOGIN) === 'true';
  }

  /**
   * `msal.idtoken` and `msal.accToken` are written by `GraphService`, not by MSAL — they only
   * borrow the `msal.` prefix (see the note on `Identifier`). A completed `logoutPopup()` happens
   * to sweep them because its cache clear matches on that prefix, but relying on that is relying
   * on MSAL's internals to tidy up keys this app owns.
   *
   * They are exactly the keys that let a session come back: `AuthComponent.ssoAuthentication()`
   * exchanges `msal.accToken` for a Central Office token, and `msal.idtoken` is the app's JWT
   * `tokenGetter`. Observed on a real stack mid-logout, both were still present after MSAL had
   * already dropped its own account keys — so an interrupted sign-out left the one credential
   * needed to re-authenticate. Clear them here, where they belong to us.
   */
  private clearGraphTokens(): void {
    localStorage.removeItem(Identifier.TYPE_MSAL_ID_TOKEN);
    localStorage.removeItem(Identifier.TYPE_MSAL_ACC_TOKEN);
  }

  /**
   * Ends the session and lands on `/auth`.
   *
   * @param preventBroadcast do not tell the other tabs to sign out (the idle-timeout path).
   */
  logout(preventBroadcast = false): void {
    if (!this.isSsoSession()) {
      this.startupService.logout(preventBroadcast);
      return;
    }
    this.logoutSsoUser(preventBroadcast).subscribe();
  }

  /**
   * Returned rather than only subscribed so tests can drive it and callers can sequence on it.
   *
   * A cancelled account picker leaves the MSAL cache — and the AAD cookie behind it — untouched,
   * so signing out of Central Office alone would bounce straight back in through the silent
   * re-auth on `/auth`. Cancelling therefore leaves the user where they are, which is also what
   * dismissing a sign-out prompt should mean. Any other failure still ends the CO session, so a
   * blocked or broken popup cannot trap someone in a session they asked to leave.
   */
  logoutSsoUser(preventBroadcast = false): Observable<unknown> {
    this.graphService.ssoAuthenticated$.next(SSOLoginStatus.Logout);

    // Passing the active account skips Azure AD's account picker: there is only ever one account
    // here, and the extra click was a second chance to cancel a sign-out already confirmed.
    const account = this.msalService.instance.getActiveAccount() ?? undefined;

    return this.msalService.logoutPopup({ account }).pipe(
      tap(() => {
        this.clearGraphTokens();
        this.startupService.clearSession(preventBroadcast);
        this.startupService.redirectToAuth();
      }),
      catchError((error: { errorCode?: string }) => {
        if (error?.errorCode === 'user_cancelled') {
          this.logger.info('[SsoLogoutService] Sign-out cancelled in the Azure AD popup; session kept.');
          return EMPTY;
        }
        this.logger.error('[SsoLogoutService] Azure AD sign-out failed; ending the local session anyway.', error);
        this.clearGraphTokens();
        this.startupService.clearSession(preventBroadcast);
        this.startupService.redirectToAuth();
        return of(null);
      })
    );
  }
}
